/**
 * How far a ball actually goes — the model every AI lead has to be solved from.
 *
 * ⚠ THE GROUND LAW ITSELF MOVED (2026-08-08). `groundDecel`, `travelForTime`,
 * `travelForDistance` and `groundRunOut` now live in `sim/ground-travel.ts` and
 * are re-exported below, unchanged and byte-for-byte. They had to move because
 * the STRIKE path needs them too: `ball.ts`'s pass lead was solved from
 * `distance / launchSpeed` — the very `v·t` this file's header calls "wrong by
 * many metres" — and a `/sim` module may not depend on `/ai`. Everything that
 * imported them from here still works; this file keeps the AI-facing half
 * (`predictBall`, `solveIntercept`) and its own test suite.
 *
 * ── Why this file exists ────────────────────────────────────────────────────
 *
 * `BALL_ROLLING_RESISTANCE` (0.02) says a grounded ball loses 0.2 m/s². That
 * number is real but it is almost never the one in force. While the ball is on
 * the turf, contact resolves EVERY tick with a normal speed of about g·dt, so
 * `applyContactFriction` fires every tick and takes a Coulomb bite out of the
 * tangential slip — roughly 30x rolling resistance. MEASURED through the real
 * pipeline (`ai/ball-travel.test.ts` pins these against a stepped ball):
 *
 *   launched at   travels in 1.5 s   speed at 1.5 s
 *        6 m/s          3.71 m           0.83 m/s
 *       10 m/s          7.28 m           1.66 m/s
 *       14 m/s         11.98 m           2.92 m/s
 *       18 m/s         17.06 m           5.18 m/s
 *       22 m/s         21.87 m           7.91 m/s
 *
 * A 10 m/s ground pass therefore travels about **9 m in total** and stops. Any
 * lead, intercept or pass weight computed as `v·t` — or from rolling resistance
 * — is wrong by many metres, and the two symptoms are exactly the ones the owner
 * reported: chasers arriving where the ball ISN'T, and passes that are not
 * playable when they get there.
 *
 * ── The law, and how it was obtained ────────────────────────────────────────
 *
 * `groundDecel(v)` below is a two-branch fit to the deceleration the sim really
 * applies, sampled tick-by-tick off a 24 m/s roll:
 *
 *   v 23.6 → 12.52   v 15.0 → 9.30   v 10.4 → 8.56   v 4.0 → 7.00
 *   v  3.7 →  4.13   v  2.0 → 2.28   v  1.1 → 1.28
 *
 * There is a genuine DISCONTINUITY just under 4 m/s: above it the ball is
 * skidding and the Coulomb cap dominates (a base plus a v² aero term); below it
 * the ball has spun up enough that the slip — and so the friction — collapses,
 * and the decay becomes proportional to speed. The fit reproduces every sample
 * above to within ~9 %.
 *
 * ── Sim purity ──────────────────────────────────────────────────────────────
 *
 * Pure functions of their arguments. No RNG (so no draw-count change — recorded
 * replays are unaffected), no wall clock, no trig at all: only `+ - * /`, which
 * are the correctly-rounded IEEE-754 ops, so this is bit-identical on every
 * engine for free (the same argument as `dpow025`, DECISIONS.md D2). Results are
 * returned through caller-supplied out-objects; nothing allocates. (§15.7)
 *
 * ── What is DELIBERATELY still wrong ───────────────────────────────────────
 *
 * Magnus is not modelled and neither is the drag crisis. That is not an
 * oversight, it is §10.2: a curling or dipping ball must still wrong-foot the
 * AI exactly as it beats `goalkeeper.ts`'s own approximation. What this file
 * fixes is the boring case — a ball rolling along the grass — which a real
 * player reads almost perfectly, and which is why he arrives on it.
 */

import { BALL_RADIUS, BALL_TRAVEL_MAX_HORIZON_S, GRAVITY } from '../sim/constants.js';
import {
  type BallTravel,
  createBallTravel,
  groundDecel,
  groundRunOut,
  travelForDistance,
  travelForTime,
} from '../sim/ground-travel.js';

// The ground law lives in /sim (see the header). Re-exported so every existing
// importer of `ai/ball-travel.js` is unaffected.
export {
  type BallTravel,
  createBallTravel,
  groundDecel,
  groundRunOut,
  travelForDistance,
  travelForTime,
};

/** Where a ball will be, and how fast, after `t` seconds. Out-params only. */
export interface BallPredict {
  x: number;
  z: number;
  /** Horizontal speed at that moment. */
  speed: number;
}

export function createBallPredict(): BallPredict {
  return { x: 0, z: 0, speed: 0 };
}

const travelScratch = createBallTravel();

/**
 * Predict the ball's horizontal position `t` seconds from now.
 *
 * Grounded: the real skid law above. AIRBORNE: the horizontal velocity is held
 * constant until touchdown (air drag over a sub-second hop is small next to the
 * error this replaces) and the skid law takes over from the landing point. The
 * bounce itself is not modelled, so an airborne prediction is slightly SHORT —
 * deliberately the safe direction, since a chaser who is a little in front of a
 * bouncing ball still gets it, and one who is behind it never does.
 */
export function predictBall(
  px: number,
  py: number,
  pz: number,
  vx: number,
  vy: number,
  vz: number,
  t: number,
  out: BallPredict,
): void {
  const hSpeed = Math.sqrt(vx * vx + vz * vz);
  if (hSpeed < 1e-6) {
    out.x = px;
    out.z = pz;
    out.speed = 0;
    return;
  }
  const ux = vx / hSpeed;
  const uz = vz / hSpeed;

  // Time to touchdown, from py + vy·t + ½g t² = BALL_RADIUS (g is negative).
  let airT = 0;
  if (py > BALL_RADIUS + 1e-3) {
    const h = py - BALL_RADIUS;
    const disc = vy * vy - 2 * GRAVITY * h; // −2·g·h > 0 for g<0
    const root = disc > 0 ? Math.sqrt(disc) : 0;
    // Positive root of ½g t² + vy t − h = 0 with g<0.
    airT = (-vy - root) / GRAVITY;
    if (!(airT > 0)) airT = 0;
  }

  if (t <= airT) {
    out.x = px + vx * t;
    out.z = pz + vz * t;
    out.speed = hSpeed;
    return;
  }
  const landX = px + vx * airT;
  const landZ = pz + vz * airT;
  travelForTime(hSpeed, t - airT, travelScratch);
  out.x = landX + ux * travelScratch.dist;
  out.z = landZ + uz * travelScratch.dist;
  out.speed = travelScratch.speed;
}

/** An intercept solution: where to run, and when you would get there. */
export interface Intercept {
  x: number;
  z: number;
  /** Seconds until the meeting. */
  t: number;
  /** Ball speed at the meeting point. */
  ballSpeed: number;
  /**
   * True when the ball comes to rest before the runner can reach it, i.e. the
   * meeting point is a STATIONARY ball. Nothing is wrong with that — it just
   * means the "where will it be" question has a fixed answer.
   */
  ballAtRest: boolean;
}

export function createIntercept(): Intercept {
  return { x: 0, z: 0, t: 0, ballSpeed: 0, ballAtRest: false };
}

const predictScratch = createBallPredict();

/**
 * Solve "where do I meet this ball" for a runner at (rx, rz) closing at
 * `runSpeed`, against the REAL travel model.
 *
 * Fixed-point on t = |runner → ball(t)| / runSpeed, exactly as before — what
 * changed is that ball(t) is now the ball. `iters` passes; four converges to
 * centimetres for the speeds football produces.
 */
export function solveIntercept(
  rx: number,
  rz: number,
  bx: number,
  by: number,
  bz: number,
  bvx: number,
  bvy: number,
  bvz: number,
  runSpeed: number,
  iters: number,
  out: Intercept,
): void {
  const speed = runSpeed > 0.1 ? runSpeed : 0.1;
  let t = 0;
  for (let i = 0; i < iters; i++) {
    predictBall(bx, by, bz, bvx, bvy, bvz, t, predictScratch);
    const dx = predictScratch.x - rx;
    const dz = predictScratch.z - rz;
    t = Math.sqrt(dx * dx + dz * dz) / speed;
    if (t > BALL_TRAVEL_MAX_HORIZON_S) t = BALL_TRAVEL_MAX_HORIZON_S;
  }
  predictBall(bx, by, bz, bvx, bvy, bvz, t, predictScratch);
  out.x = predictScratch.x;
  out.z = predictScratch.z;
  out.t = t;
  out.ballSpeed = predictScratch.speed;
  out.ballAtRest = predictScratch.speed <= 0;
}
